import React from "react";
import { Link } from "react-router-dom";
import { Pagination } from "../UI";
import { formatPosition } from "../UI/DesignStandards";
import { AllEventScoreCell } from "./components/AllEventScoreCell";
import { tournamentPath } from "../../utility/tournamentUrls";
import { useTournamentSlug } from "../../context/TournamentRouteContext";

const headerClass =
  "px-1.5 sm:px-2 md:px-3 py-2 sm:py-3 text-center font-display font-bold tracking-wider uppercase text-brand-charcoal bg-white text-xs md:text-sm";

const renderTableHeaders = (columns, showPayouts, eventName) => {
  if (!columns || !Array.isArray(columns)) return null;

  return (
    <>
      {columns.map((col) => (
        <th
          key={col.key}
          className={`${headerClass} ${
            col.key === "position"
              ? "w-16 sm:w-20"
              : col.key === "teamScore"
              ? "w-20 sm:w-24"
              : ""
          }`}
          scope="col"
          aria-label={`${col.label} for ${eventName || "tournament event"}`}
        >
          {col.label}
        </th>
      ))}
      {showPayouts && (
        <th
          className={`${headerClass} w-20 sm:w-24`}
          scope="col"
          aria-label={`Prize amount for ${eventName || "tournament event"}`}
        >
          Prize
        </th>
      )}
    </>
  );
};

export const TournamentResultsTable = ({
  eventData,
  showPayouts,
  tournamentId,
  pagination,
  onPageChange,
  onItemsPerPageChange,
  eventName,
  eventId,
}) => {
  const tournamentSlug = useTournamentSlug();
  // Minimal sanity check (kept quiet)
  // Validate data
  if (!eventData || !Array.isArray(eventData)) {
    return null;
  }

  if (eventData.length === 0) {
    return (
      <div
        className="text-center py-8 text-brand-muted"
        role="status"
        aria-label={`No results available for ${eventName || "this event"}`}
      >
        No results available for this event.
        <div className="sr-only" aria-live="polite">
          {eventName}: No results available.
        </div>
      </div>
    );
  }

  // Generate columns based on the original 4-column structure
  const generateColumns = () => {
    // Check if this is an individual event by looking at the first entry
    const isIndividualEvent =
      eventData.length > 0 && eventData[0]?.isIndividualEvent;

    const columns = [
      { key: "position", label: "Position" },
      { key: "teamName", label: isIndividualEvent ? "Name" : "Team Name" },
      { key: "teamScore", label: isIndividualEvent ? "Score" : "Team Score" },
    ];

    // Prize column is handled separately in the table rendering
    return columns;
  };

  const columns = generateColumns();

  // Transform data for table rendering - matching original structure
  const transformDataForTable = (data) => {
    return data.map((team) => {
      let teamName, teamScore, teamId;

      if (team.isIndividualEvent) {
        // For individual events, use bowler name and individual scores
        teamName = team.bowlerName;
        teamScore = team.individualHandicapTotal || team.individualScratchTotal;
        teamId = team.teamId; // Use team ID for individual events (bowler's team)
      } else {
        // For team events, determine team name based on bowler quantity
        teamName = team.teamName;
        if (team.bowlers && team.bowlers.length > 0) {
          if (team.bowlers.length === 1) {
            // Single bowler: just the person's name
            teamName = team.bowlers[0].name;
          } else if (team.bowlers.length === 2) {
            // Two bowlers: "First Name and Second Name"
            teamName = `${team.bowlers[0].name} and ${team.bowlers[1].name}`;
          }
          // 3+ bowlers: keep the team name as is
        }
        teamScore = team.teamHandicapTotal || team.teamScratchTotal;
        teamId = team.teamId;
      }

      const transformedRow = {
        position: team.position,
        teamName: teamName,
        teamId: teamId,
        teamScore: teamScore,
        prize: team.prize || 0,
        isTied: team.isTied || false,
        tieDescription: team.tieDescription,
        isIndividualEvent: team.isIndividualEvent || false,
        // Preserve all event metadata for display
        allEventMetadata: team.allEventMetadata,
        bowlerId: team.bowlerId,
        bowlerName: team.bowlerName,
        eventName: team.eventName,
      };

      return transformedRow;
    });
  };

  const tableData = transformDataForTable(eventData);

  return (
    <div
      className="space-y-4"
      role="region"
      aria-label={`${eventName || "Tournament event"} results table`}
    >
      <div
        className="w-full overflow-x-auto"
        style={{
          WebkitOverflowScrolling: "touch",
        }}
      >
        <table
          className="w-full divide-y divide-brand-charcoal/10"
          role="table"
          aria-label={`${eventName || "Tournament event"} results`}
          aria-describedby={`table-summary-${eventId || "event"}`}
        >
          <caption className="sr-only">
            {eventName || "Tournament event"} results showing position, name,
            score, and prize information
          </caption>
          <thead className="bg-white border-b border-brand-charcoal/10">
            <tr role="row">
              {renderTableHeaders(columns, showPayouts, eventName)}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-brand-charcoal/10">
            {tableData.map((row, index) => (
              <tr
                key={index}
                className={
                  index % 2 === 0 ? "bg-white" : "bg-brand-cream/60"
                }
                role="row"
                aria-label={`${row.position}${
                  row.position === 1
                    ? "st"
                    : row.position === 2
                    ? "nd"
                    : row.position === 3
                    ? "rd"
                    : "th"
                } place: ${row.teamName}`}
              >
                {columns.map((column, colIndex) => {
                  const positionText =
                    column.key === "position"
                      ? typeof row[column.key] === "string" &&
                        row[column.key].includes("Ineligible")
                        ? "Ineligible"
                        : formatPosition(row[column.key])
                      : null;

                  return (
                    <td
                      key={`${row.teamId}-${column.key}-${colIndex}`}
                      className={`px-1.5 sm:px-2 md:px-3 py-2 sm:py-3 text-center ${
                        column.key === "position"
                          ? "w-16 sm:w-20 whitespace-nowrap font-score text-sm sm:text-base md:text-lg"
                          : column.key === "teamScore"
                          ? "w-20 sm:w-24 whitespace-nowrap font-score text-sm sm:text-base md:text-lg"
                          : column.key === "teamName"
                          ? "min-w-0 text-sm md:text-sm truncate"
                          : "text-xs md:text-sm"
                      }`}
                      role="cell"
                      aria-label={`${column.label}: ${
                        row[column.key] || "Not available"
                      }`}
                      title={
                        column.key === "teamName"
                          ? row[column.key] || ""
                          : column.key === "position" &&
                            typeof row[column.key] === "string" &&
                            row[column.key].includes("Ineligible")
                          ? row[column.key]
                          : undefined
                      }
                    >
                      {column.key === "teamName" ? (
                        row.teamId ? (
                          <Link
                            to={`${tournamentPath(tournamentSlug || tournamentId, "/scoring")}?team=${row.teamId}`}
                            className="text-brand-charcoal font-medium hover:text-brand-gold-ink hover:underline truncate block"
                            aria-label={`View scoring details for ${row.teamName}`}
                            title={`View scoring details for ${row.teamName}`}
                          >
                            {row[column.key] || "-"}
                          </Link>
                        ) : (
                          <span className="truncate block">
                            {row[column.key] || "-"}
                          </span>
                        )
                      ) : column.key === "position" ? (
                        <div className="flex flex-col items-center">
                          <span className="text-xs">{positionText}</span>
                          {row.isTied && (
                            <span className="text-[9px] sm:text-[10px] text-brand-muted font-medium">
                              Tie
                            </span>
                          )}
                        </div>
                      ) : column.key === "teamScore" ? (
                        <AllEventScoreCell
                          score={row[column.key]}
                          allEventMetadata={row.allEventMetadata}
                          eventName={row.eventName}
                          bowlerName={row.bowlerName}
                          tournamentId={tournamentId}
                        />
                      ) : (
                        row[column.key] || "-"
                      )}
                    </td>
                  );
                })}
                {showPayouts && (
                  <td
                    className="px-1.5 sm:px-2 md:px-3 py-2 sm:py-3 whitespace-nowrap text-xs md:text-sm text-center w-20 sm:w-24"
                    role="cell"
                    aria-label={`Prize: ${
                      row.prize > 0 ? `$${row.prize.toFixed(2)}` : "No prize"
                    }${row.isTied ? ` - ${row.tieDescription || "Tied"}` : ""}`}
                  >
                    {(() => {
                      return null;
                    })()}
                    {row.prize > 0 ? (
                      <div className="flex flex-col">
                        <div className="font-score font-medium">
                          ${row.prize.toFixed(2)}
                        </div>
                        {row.isTied && row.tieDescription && (
                          <div className="text-[9px] sm:text-xs text-brand-muted">
                            {row.tieDescription}
                          </div>
                        )}
                      </div>
                    ) : (
                      "-"
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Table summary for screen readers */}
      <div
        id={`table-summary-${eventId || "event"}`}
        className="sr-only"
        aria-live="polite"
      >
        {eventName}: {tableData.length} results displayed.{" "}
        {showPayouts
          ? "Prize information included."
          : "Prize information not shown."}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalItems > 0 && (
        <nav
          className="border-t border-brand-charcoal/10 px-4 py-4"
          role="navigation"
          aria-label={`Pagination for ${
            eventName || "tournament event"
          } results`}
        >
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            itemsPerPage={pagination.itemsPerPage}
            onPageChange={onPageChange}
            onItemsPerPageChange={onItemsPerPageChange}
          />
        </nav>
      )}
    </div>
  );
};
